import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Bike, ChefHat, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import LeafletMap from "../components/LeafletMap";

const STAGES = [
  { key: "Order Confirmed", label: "Confirmed", Icon: Check },
  { key: "Preparing Food", label: "Preparing", Icon: ChefHat },
  { key: "Out for Delivery", label: "On the way", Icon: Bike },
  { key: "Delivered", label: "Delivered", Icon: CheckCircle2 },
];

// Map any historic/legacy status to its display stage
function stageIndex(status) {
  if (status === "Delivered") return 3;
  if (status === "Out for Delivery" || status === "On the Way" || status === "Picked Up by Rider") return 2;
  if (status === "Preparing Food" || status === "Ready for Pickup") return 1;
  if (status === "Order Confirmed") return 0;
  return -1;
}

export default function TrackingPage() {
  const { orderId } = useParams();
  const [track, setTrack] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/tracking/${orderId}`);
        if (!stop) setTrack(data);
      } catch (e) { setErr("Order not found"); }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [orderId]);

  if (err) return <div className="max-w-xl mx-auto py-20 text-center" data-testid="tracking-page">{err}</div>;
  if (!track) return <div className="max-w-xl mx-auto py-20 text-center text-gray-500">Loading…</div>;

  const { order, restaurant } = track;
  const currentIdx = stageIndex(order.status);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="tracking-page">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Tracking order</div>
      <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter">#{order.id.slice(0, 8).toUpperCase()}</h1>

      <div className="mt-6 rounded-2xl overflow-hidden border border-gray-100">
        <LeafletMap
          restaurant={{ lat: restaurant.lat, lng: restaurant.lng }}
          user={{ lat: order.address.lat, lng: order.address.lng }}
          height={380}
        />
      </div>

      {/* Status bar */}
      <div className="mt-6 bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className={`w-2.5 h-2.5 rounded-full ${order.status === "Delivered" ? "bg-green-500" : order.status === "Cancelled" ? "bg-red-500" : "bg-[#C85A5A] mj-pulse"}`} />
          <div className="font-display font-bold text-lg" data-testid="order-status">{order.status}</div>
        </div>
        <div className="overflow-x-auto hide-scrollbar">
          <div className="min-w-[420px] grid grid-cols-4 gap-3">
            {STAGES.map((s, i) => (
              <div key={s.key} className="flex flex-col items-center text-center">
                <div className={`w-10 h-10 rounded-full grid place-items-center ${i <= currentIdx ? "bg-black text-white" : "bg-gray-100 text-gray-400"}`}>
                  <s.Icon className="w-4 h-4" />
                </div>
                <div className={`text-[11px] mt-1.5 font-bold ${i <= currentIdx ? "text-black" : "text-gray-400"}`}>{s.label}</div>
                {i < STAGES.length - 1 && (
                  <div className={`h-0.5 w-full mt-1 ${i < currentIdx ? "bg-black" : "bg-gray-200"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Delivery address */}
      <div className="mt-6 bg-white border border-gray-100 rounded-2xl p-5" data-testid="delivering-to-card">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">Delivering to</div>
        <div className="text-sm">{order.address.label}</div>
        <div className="text-sm text-gray-600">{order.address.line1}, {order.address.city}</div>
      </div>

      {/* Items */}
      <div className="mt-6 bg-white border border-gray-100 rounded-2xl p-5">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">Order items</div>
        <div className="space-y-2">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm">
              <div>{it.qty} × {it.name}{it.variant ? <span className="text-gray-500"> ({it.variant})</span> : null}</div>
              <div>₹{it.price * it.qty}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-dashed border-gray-200 space-y-1.5 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Food Total</span>
            <span>₹{order.subtotal}</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Delivery Charge</span>
            <span>₹{order.delivery_fee}</span>
          </div>
          <div className="flex justify-between font-black pt-2 border-t border-dashed border-gray-200">
            <span>{order.payment_method === "COD" ? "COD Payable" : "Total Paid"}</span>
            <span>₹{order.total}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
